/**
 * Leaderboard screen model: UI state → backend query, podium / list split,
 * scope labels and rank chips (also used by the game-over screen).
 *
 * The backend fills a scope's location from the caller's own profile
 * (services/backend/api-types.ts § LeaderboardSearch), so the query only names
 * the scope. This module decides up front when a scope can't work yet (an
 * anonymous player has no school board) so the screen can say why instead of
 * showing an empty board.
 */
import type { Locale } from '@dropstack/core';
import type {
  LeaderboardEntry,
  LeaderboardMetric,
  LeaderboardPage,
  LeaderboardPeriod,
  LeaderboardQuery,
  LeaderboardScope,
  PlayerProfile,
} from '../../contracts.ts';
import { LEADERBOARD_SCOPES } from '../../services/backend/api-types.ts';
import { countryName, provinceName } from '../../services/geo.ts';

export { LEADERBOARD_SCOPES };

/** Rows fetched per board: enough to scroll through, small enough to stay quick. */
export const BOARD_LIMIT = 50;

export interface BoardUiState {
  metric: LeaderboardMetric;
  scope: LeaderboardScope;
  period: LeaderboardPeriod;
  /** Optional grade filter; for the "grade" scope it picks the grade (default: the player's). */
  grade: number | null;
}

export const DEFAULT_BOARD_UI: BoardUiState = { metric: 'rated', scope: 'global', period: 'all', grade: null };

export type BoardRequest =
  | { ok: true; query: LeaderboardQuery }
  /** needs-profile: join / finish the profile to see this board. needs-grade: pick a grade first. */
  | { ok: false; reason: 'needs-profile' | 'needs-grade' };

/** Translate the screen's controls into a LeaderboardQuery, or say why that board isn't available. */
export function buildBoardRequest(ui: BoardUiState, profile: PlayerProfile | null): BoardRequest {
  const base: LeaderboardQuery = { metric: ui.metric, scope: ui.scope, period: ui.period, limit: BOARD_LIMIT };
  if (ui.scope === 'grade') {
    const grade = ui.grade ?? profile?.grade ?? null;
    return grade === null ? { ok: false, reason: 'needs-grade' } : { ok: true, query: { ...base, grade } };
  }
  if (ui.scope !== 'global' && !scopeValue(ui.scope, profile)) return { ok: false, reason: 'needs-profile' };
  return { ok: true, query: ui.grade === null ? base : { ...base, grade: ui.grade } };
}

/** The profile value a location scope filters on ('' when unknown). */
function scopeValue(scope: Exclude<LeaderboardScope, 'global' | 'grade'>, profile: PlayerProfile | null): string {
  if (!profile) return '';
  switch (scope) {
    case 'country':
      return profile.country;
    case 'province':
      return profile.province;
    case 'city':
      return profile.city;
    case 'school':
      return profile.schoolId ?? '';
  }
}

/** i18n key of each scope's generic label ("Country", "School", …). */
export const SCOPE_LABEL_KEYS: Readonly<Record<LeaderboardScope, string>> = {
  global: 'shell.scopeGlobal',
  country: 'shell.scopeCountry',
  province: 'shell.scopeProvince',
  city: 'shell.scopeCity',
  school: 'shell.scopeSchool',
  grade: 'shell.scopeGrade',
};

/**
 * The player's own place for a scope, localised ("Suid-Afrika", "Wes-Kaap",
 * "Baobab High School"), or null when there is no specific name to show.
 * For the grade scope the caller formats the grade number itself.
 */
export function scopePlaceName(scope: LeaderboardScope, profile: PlayerProfile | null, locale: Locale): string | null {
  if (!profile) return null;
  switch (scope) {
    case 'global':
    case 'grade':
      return null;
    case 'country':
      return profile.country ? countryName(profile.country, locale) : null;
    case 'province':
      return profile.province ? provinceName(profile.province, locale) : null;
    case 'city':
      return profile.city || null;
    case 'school':
      return profile.schoolName || null;
  }
}

export interface RankChip {
  scope: LeaderboardScope;
  rank: number;
  /** Specific place name, when known (see scopePlaceName). */
  place: string | null;
  /** The grade, for the grade scope. */
  grade: number | null;
}

/** Rank chips in canonical scope order (global → grade), skipping scopes without a rank. */
export function rankChips(
  ranks: Partial<Record<LeaderboardScope, number>>,
  profile: PlayerProfile | null,
  locale: Locale,
): RankChip[] {
  return LEADERBOARD_SCOPES.flatMap((scope): RankChip[] => {
    const rank = ranks[scope];
    if (rank === undefined || !Number.isFinite(rank) || rank < 1) return [];
    return [{ scope, rank, place: scopePlaceName(scope, profile, locale), grade: scope === 'grade' ? (profile?.grade ?? null) : null }];
  });
}

/** Top three (by competition rank, so ties can share a step) and everyone after them. */
export function splitPodium(entries: readonly LeaderboardEntry[]): { podium: LeaderboardEntry[]; rest: LeaderboardEntry[] } {
  const podium = entries.slice(0, 3).filter((e) => e.rank <= 3);
  return { podium, rest: entries.slice(podium.length) };
}

/** The podium step (1–3) an entry stands on: its rank, so tied players share a height. */
export function podiumStep(entry: LeaderboardEntry): 1 | 2 | 3 {
  return entry.rank <= 1 ? 1 : entry.rank === 2 ? 2 : 3;
}

/** The caller's row to pin at the bottom of the board, or null when they aren't ranked on it. */
export function pinnedRow(page: LeaderboardPage | null): LeaderboardEntry | null {
  return page?.me ?? null;
}

/** Whether the player's scores can appear on boards at all. */
export function isOnBoards(profile: PlayerProfile | null): boolean {
  return Boolean(profile?.onboarded && profile.leaderboardOptIn);
}
